/**
 * The view of "Price curve of a date" ↔ the URL and the remembered view (CU-869fmjzrg D-F, CA 4-5). Two views share
 * Rental length, Cars, Min/Avg/Max and Tier/Providers; each keeps its own offices and dates (UX § 1). A relative pick-up
 * travels as its key, never as a date.
 *
 * Pure and alias-free: `node --test` runs it.
 */
import { fromIso, toIso } from '../../../@services/dates.js'
import { NO_CARS, CARS, parseCars } from '../../../@services/cars.js'
import { flag, idList, positiveInt } from '../../../@services/params.js'
import { DEFAULT_WHEN, MAX_IN_DAYS, PICKS, isWhen } from './pickup.js'

export const VIEWS = Object.freeze(['curve', 'weekly'])
export const STATS = Object.freeze(['min', 'avg', 'max'])
export const OFFICE_KINDS = Object.freeze(['offices', 'groups'])
export const SITE_KINDS = Object.freeze(['sites', 'siteGroups', 'markets'])
export const PRICES = Object.freeze(['total', 'daily'])
const MAX_DATES = 31

// CA 4: last office used in BI (set by the page), 7 days, Next Saturday, all cars, against last year (same weekday)
export const DEFAULT_VIEW = Object.freeze({
	view: 'curve', office: null, lor: 7, ...NO_CARS, tier: null, providers: [], stat: 'min', same: false, ly: true,
	pick: 'date', when: DEFAULT_WHEN.date, n: null, date: null,
	// Week by week
	okind: 'offices', offices: [], dates: [], weeks: 'default', skind: 'sites', sites: [], trans: [], cur: null, price: 'total',
})

const isoList = v => {
	const out = String(v || '').split(',').filter(Boolean).map(fromIso)
	return out.length && out.length <= MAX_DATES && out.every(Boolean) ? [...new Set(out.map(toIso))].sort() : null
}
const weekList = v => (v === 'default' ? 'default' : (() => {
	const keys = String(v || '').split(',').filter(Boolean)
	return keys.length && keys.length <= 5 && keys.every(k => /^\d{6}$/.test(k)) ? [...new Set(keys)].sort() : null
})())
const textList = v => {
	const out = String(v || '').split(',').map(s => s.trim()).filter(Boolean)
	return out.length && out.length <= 50 && out.every(s => s.length <= 20) ? [...new Set(out)] : null
}

/** The valid keys of a URL or a stored view. A whole view (it has `view`) says its filters even when it has none. */
export function parseView(params) {
	const get = k => (typeof params?.get === 'function' ? params.get(k) : params?.[k])
	const out = {}
	if (VIEWS.includes(get('view'))) out.view = get('view')
	const office = positiveInt(get('office'))
	if (office) out.office = office
	const lor = positiveInt(get('lor'))
	if (lor) out.lor = lor
	if (STATS.includes(get('stat'))) out.stat = get('stat')
	for (const k of ['same', 'ly']) {
		const v = flag(get(k))
		if (v !== null) out[k] = v
	}

	const pick = PICKS.includes(get('pick')) ? get('pick') : null
	if (pick) {
		const when = isWhen(pick, get('when')) ? get('when') : DEFAULT_WHEN[pick]
		Object.assign(out, { pick, when, n: null, date: null })
		if (when === 'inN') {
			const n = Number(get('n'))
			if (Number.isInteger(n) && n >= 0 && n <= MAX_IN_DAYS) out.n = n
			else Object.assign(out, { when: DEFAULT_WHEN[pick] })
		}
		if (when === 'fixed') {
			const d = fromIso(get('date'))
			if (d) out.date = toIso(d)
			else Object.assign(out, { when: DEFAULT_WHEN[pick] })
		}
	}

	const whole = !!get('view')
	if (get('cars') || whole) Object.assign(out, get('cars') ? parseCars(get('cars'), get('carsIds')) : { ...NO_CARS })
	const tier = positiveInt(get('tier'))
	const providers = idList(get('providers'))
	if (tier) Object.assign(out, { tier, providers: [] })
	else if (providers) Object.assign(out, { tier: null, providers })
	else if (whole) Object.assign(out, { tier: null, providers: [] })

	// Week by week
	if (OFFICE_KINDS.includes(get('okind'))) out.okind = get('okind')
	const offices = idList(get('offices'), 50)
	if (offices) out.offices = offices
	const dates = isoList(get('dates'))
	if (dates) out.dates = dates
	const weeks = weekList(get('weeks'))
	if (weeks) out.weeks = weeks
	if (SITE_KINDS.includes(get('skind'))) out.skind = get('skind')
	const sites = (out.skind || 'sites') === 'markets' ? textList(get('sites')) : idList(get('sites'), 100)
	if (sites) out.sites = sites
	const trans = idList(get('trans'), 5)
	if (trans) out.trans = trans
	const cur = positiveInt(get('cur'))
	if (cur) out.cur = cur
	if (PRICES.includes(get('price'))) out.price = get('price')
	return out
}

/** The view: first the URL, then what was stored, then the defaults */
export function resolveView(...sources) {
	const view = { ...DEFAULT_VIEW }
	for (const source of [...sources].reverse()) Object.assign(view, parseView(source || {}))
	return view
}

/** View → query string; a relative pick-up never carries a date */
export function viewToParams(view) {
	const p = new URLSearchParams()
	p.set('view', view.view)
	if (view.office) p.set('office', String(view.office))
	if (view.lor) p.set('lor', String(view.lor))
	p.set('stat', view.stat)
	p.set('same', view.same ? '1' : '0')
	p.set('ly', view.ly ? '1' : '0')
	p.set('pick', view.pick)
	p.set('when', view.when)
	if (view.when === 'inN' && Number.isInteger(view.n)) p.set('n', String(view.n))
	if (view.when === 'fixed' && view.date) p.set('date', view.date)
	if (view.cars && view.cars !== CARS.ALL && view.carsIds?.length) {
		p.set('cars', view.cars)
		p.set('carsIds', view.carsIds.join(','))
	}
	if (view.tier) p.set('tier', String(view.tier))
	else if (view.providers?.length) p.set('providers', view.providers.join(','))
	if (view.okind !== 'offices') p.set('okind', view.okind)
	if (view.offices?.length) p.set('offices', view.offices.join(','))
	if (view.dates?.length) p.set('dates', view.dates.join(','))
	p.set('weeks', view.weeks === 'default' ? 'default' : view.weeks.join(','))
	if (view.skind !== 'sites') p.set('skind', view.skind)
	if (view.sites?.length) p.set('sites', view.sites.join(','))
	if (view.trans?.length) p.set('trans', view.trans.join(','))
	if (view.cur) p.set('cur', String(view.cur))
	if (view.price !== 'total') p.set('price', view.price)
	return p.toString()
}

export const viewToStored = view => Object.fromEntries(new URLSearchParams(viewToParams(view)))
export const storageKey = uid => `rentacar.dashboards.priceCurve.${uid || 'anon'}`
export const peNoticeKey = uid => `rentacar.dashboards.priceCurve.peNotice.${uid || 'anon'}`

